/**
 * One number-formatting convention for the whole app: `1,234,567.89`.
 *
 * The app used to mix three of them: bare `toLocaleString()` (browser locale —
 * whatever the operator's OS says), explicit `'vi-VN'` (1.234,5) and `toFixed()`
 * (1234.5, no grouping). Side by side that reads as garbage: a PnL of 2040
 * thousand VND rendered "2.040" next to a price rendered "1891.6" — same dot,
 * one a group separator and one a decimal point.
 *
 * The convention is FIXED, not configurable. It used to be selectable per
 * operator, which reintroduced the same class of bug from the other end: no
 * numeric input in the app passes antd's `decimalSeparator`, so every one of
 * them accepts a dot and only a dot. Choosing comma-decimal meant reading
 * "1.891,6" and having to type "1891.6" into the field right next to it. On a
 * screen where the numbers are money, a display that disagrees with its own
 * input is worse than an unfamiliar separator.
 *
 * Dot-decimal is the side that has to win, because the input side cannot move:
 * `decimalSeparator` would have to be threaded through every InputNumber, and
 * the values still travel to the API as plain JSON numbers.
 */

/** Thousands separator. */
export const GROUP_SEPARATOR = ',';
/** Decimal point — the same character every numeric input accepts. */
export const DECIMAL_SEPARATOR = '.';

/**
 * The locale whose punctuation already matches the convention, so `Intl` can own
 * digits and rounding without also owning the separators.
 */
const PINNED_LOCALE = 'en-US';

/** What `Intl` itself defaults to, so `formatNumber(x)` drops in for `toLocaleString()`. */
const DEFAULT_MAX_FRACTION_DIGITS = 3;

export interface FormatOptions {
    /** Exact decimals; sets both min and max. */
    digits?: number;
    minimumFractionDigits?: number;
    /** Default 3, as `Intl.NumberFormat` does. */
    maximumFractionDigits?: number;
    /** Thousands grouping. Default true. */
    grouping?: boolean;
    /** Prefix non-negative values with '+'. */
    signed?: boolean;
    /** Rendered instead of a number when the value is null/undefined/NaN. */
    fallback?: string;
}

/** The app-wide number renderer. Grouped by default, locale-independent. */
export function formatNumber(
    value: number | string | null | undefined,
    options: FormatOptions = {},
): string {
    const { digits, grouping = true, signed = false, fallback = '—' } = options;
    const n = typeof value === 'string' ? Number(value) : value;
    if (n === null || n === undefined || !Number.isFinite(n)) return fallback;

    const minimumFractionDigits = digits ?? options.minimumFractionDigits ?? 0;
    const maximumFractionDigits = Math.max(
        minimumFractionDigits,
        digits ?? options.maximumFractionDigits ?? DEFAULT_MAX_FRACTION_DIGITS,
    );

    // numberFormat-exempt: the one sanctioned call — pinned locale, not the browser's.
    const body = (n as number).toLocaleString(PINNED_LOCALE, {
        minimumFractionDigits,
        maximumFractionDigits,
        useGrouping: grouping,
    });
    // `signed` only adds the plus; Intl already carries the minus.
    return signed && (n as number) >= 0 ? `+${body}` : body;
}

/** Rounded to whole units — money in VND, quantities, capacities. */
export function formatInt(
    value: number | string | null | undefined,
    options: Omit<FormatOptions, 'digits' | 'minimumFractionDigits' | 'maximumFractionDigits'> = {},
): string {
    return formatNumber(value, { ...options, digits: 0 });
}

/** Exactly `digits` decimals, still grouped — the display replacement for toFixed. */
export function formatDecimal(
    value: number | string | null | undefined,
    digits: number,
    options: Omit<FormatOptions, 'digits'> = {},
): string {
    return formatNumber(value, { ...options, digits });
}

/** Prices: 2 decimals by default, grouped. */
export function formatPrice(
    value: number | string | null | undefined,
    digits = 2,
    options: Omit<FormatOptions, 'digits'> = {},
): string {
    return formatNumber(value, { ...options, digits });
}

/** Percentages, with the sign appended. Fallback is returned bare. */
export function formatPercent(
    value: number | string | null | undefined,
    digits = 2,
    options: Omit<FormatOptions, 'digits'> = {},
): string {
    const fallback = options.fallback ?? '—';
    const body = formatNumber(value, { ...options, digits });
    return body === fallback ? body : `${body}%`;
}

/** Up to `maxDigits` decimals, trailing zeros dropped. */
export function formatCompact(
    value: number | string | null | undefined,
    maxDigits = 2,
    options: Omit<FormatOptions, 'digits' | 'maximumFractionDigits'> = {},
): string {
    return formatNumber(value, { ...options, maximumFractionDigits: maxDigits });
}

/**
 * Abbreviated magnitude for slots a full VND figure cannot fit: 1234567890 -> "1.23B".
 *
 * Lossy on purpose, so it is only ever a display shorthand. Pair it with the exact value —
 * a `title`, a tooltip — and never use it where the operator has to reconcile a number
 * against the broker, or in anything they type back in.
 */
export function formatAbbrev(
    value: number | string | null | undefined,
    digits = 2,
    options: Omit<FormatOptions, 'digits' | 'maximumFractionDigits'> = {},
): string {
    const fallback = options.fallback ?? '—';
    const n = typeof value === 'string' ? Number(value) : value;
    if (n === null || n === undefined || !Number.isFinite(n)) return fallback;

    const abs = Math.abs(n);
    const [unit, scale] =
        abs >= 1e9 ? ['B', 1e9] : abs >= 1e6 ? ['M', 1e6] : abs >= 1e3 ? ['K', 1e3] : ['', 1];
    // Trailing zeros are dropped rather than padded: "1.20B" claims a precision this
    // deliberately does not have.
    const body = formatNumber(n / scale, { ...options, maximumFractionDigits: unit ? digits : 0 });
    return `${body}${unit}`;
}

/**
 * Inverse of `formatNumber` for text the user typed or that we rendered.
 * Returns NaN when nothing numeric is left, so callers can guard.
 */
export function parseNumber(text: string | number | null | undefined): number {
    if (typeof text === 'number') return text;
    if (text === null || text === undefined) return NaN;
    const cleaned = text.split(GROUP_SEPARATOR).join('').replace(/[^0-9.eE+-]/g, '');
    if (!cleaned || cleaned === '-' || cleaned === '+') return NaN;
    return Number(cleaned);
}

/**
 * antd InputNumber wiring: `<InputNumber {...numberInputProps()} />`. Adds
 * grouping while typing; the decimal separator already matches antd's default,
 * which is exactly why the convention is pinned to dot-decimal.
 */
export const numberInputProps = () => ({
    decimalSeparator: DECIMAL_SEPARATOR,
    formatter: (v: string | number | undefined) =>
        v === undefined || v === null || v === '' ? '' : formatNumber(Number(v), { fallback: '' }),
    parser: (v: string | undefined) => {
        const n = parseNumber(v);
        return Number.isFinite(n) ? n : 0;
    },
});
